import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { themeSettings } from "theme";
import Dashboard from "scenes/dashboard";
import Layout from "scenes/layout";
import Products from "scenes/products";
import Customers from "scenes/Customers";
import Transactions from "scenes/Transaction";
import Geography from "scenes/Geography";
import Overview from "scenes/Overview";
import Daily from "scenes/Daily";
import Monthly from "scenes/Monthly";
import Breakdown from "scenes/Breakdown";
import Admins from "scenes/Admins";
import Performance from "scenes/Performance";
import Login from "scenes/Login";
import { setLogin } from "state"; 
import { getCurrentUser } from "services/auth"; 
import Categories from 'scenes/categories';
import Fournisseurs from 'scenes/fournisseurs';
import Produits from 'scenes/produits';
import ListeFournisseurs from 'scenes/liste-fournisseurs';
import AchatsFournisseurs from 'scenes/achats-fournisseurs';
import AbonnementPage from "scenes/Abonnements/abonnementPage";
import ClientsPage from "./scenes/Clients/ClientsPage";
// import VentesPage from "scenes/Ventes/VentesPages";
import VentesHistoriquePage from "scenes/Ventes/VentesHistoriquePage";
import ConfigurationFidelite from "scenes/Configuration/ConfigurationFidelite";

// Import des pages Restaurant & Hotel
import CategoriesMenuPage from "./scenes/restaurant/categories-menu/CategoriesMenuPage";
import CategoriesBoissonsPage from "./scenes/restaurant/CategoriesBoissonsPage/CategoriesBoissonsPage";
import PlatsPage from "./scenes/restaurant/PlatsPage/PlatsPage";
import BoissonsPage from "./scenes/restaurant/BoissonsPage/BoissonsPage";
import Tables from "./scenes/restaurant/Tables/Tables";
import Chambres from "./scenes/restaurant/Chambres/Chambres";
import Commandes from "./scenes/restaurant/Commandes/Commandes";
import Locations from "./scenes/restaurant/Locations/Locations";
import Reservations from "./scenes/restaurant/Reservations/Reservations";
import Employes from "./scenes/restaurant/Employes/Employes";
import Depenses from "./scenes/restaurant/Depenses/Depenses";
import Succursale from "./depot/succursale";
import SessionsCaissePage from "./depot/sessionCaissePage";
import ManageUser from "depot/manage-user";
import StockList from "scenes/GestionStock/StockList";
// Import des pages GYM
import GymRoutes from './gym/GymRoutes'

// ========== COMPOSANTS DE ROUTE ==========

// Route protégée de base (vérifie seulement l'authentification)
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.global.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Route publique (redirige si déjà authentifié)
const PublicRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.global.isAuthenticated);
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Route avec contrôle d'accès par module
const ModuleProtectedRoute = ({ children, requiredModule = null, modules = [] }) => {
  const isAuthenticated = useSelector((state) => state.global.isAuthenticated);
  const user = useSelector((state) => state.global.user);
  
  // Charger l'utilisateur depuis localStorage si Redux n'est pas encore hydraté
  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
  
  // Vérifier l'authentification
  if (!isAuthenticated || !currentUser.id) {
    return <Navigate to="/login" replace />;
  }

  // Récupérer le module de l'utilisateur
  const userModule = currentUser.module_actif?.toLowerCase() || 'default';

  // Fonction pour vérifier l'accès
  const hasAccess = () => {
    // L'admin a accès à tout
    if (userModule === 'admin') return true;
    
    // Si aucun module requis, autoriser l'accès
    if (!requiredModule && modules.length === 0) return true;
    
    // Si un seul module est requis
    if (requiredModule) {
      // Dashboard et Inventaire accessibles à tous
      if (requiredModule === 'dashboard' || requiredModule === 'inventaire') {
        return true;
      }
      return userModule === requiredModule.toLowerCase();
    }
    
    // Si plusieurs modules sont autorisés
    if (modules.length > 0) {
      return modules.includes(userModule);
    }
    
    return false;
  };

  // Rediriger si pas d'accès
  if (!hasAccess()) {
    console.warn(`Accès refusé: module ${userModule} n'a pas accès`);
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// ========== COMPOSANT PRINCIPAL ==========

function App() {
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.global.isAuthenticated);
  
  // État de chargement
  const [isLoading, setIsLoading] = useState(true);

  // Initialiser l'authentification
  useEffect(() => {
    console.log("=== APP INITIALIZATION ===");
    
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const user = getCurrentUser();
        
        if (token && user) {
          console.log("Authentification automatique pour:", user.username);
          console.log("Module actif:", user.module_actif);
          
          dispatch(setLogin({
            token,
            user,
          }));
        } else {
          if (token) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
          }
          
          dispatch(setLogin({
            token: null,
            user: null,
          }));
        }
      } catch (error) {
        console.error("Erreur initialisation auth:", error);
        dispatch(setLogin({
          token: null,
          user: null,
        }));
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeAuth();
  }, [dispatch]);

  // Loader pendant le chargement
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: mode === 'dark' ? '#121212' : '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center',
          color: mode === 'dark' ? '#fff' : '#000'
        }}>
          <h3>Chargement de l'application...</h3>
          <p>Vérification de l'authentification</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            {/* Route publique - Login */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            {/* Routes protégées avec layout principal */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard - accessible par tous */}
              <Route path="/" element={
                <ModuleProtectedRoute requiredModule="dashboard">
                  <Navigate to="/dashboard" replace />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/dashboard" element={
                <ModuleProtectedRoute requiredModule="dashboard">
                  <Dashboard />
                </ModuleProtectedRoute>
              } />


              <Route path="depot/succursale" element={
                <ModuleProtectedRoute allowedRoles={['admin', 'proprietaire', 'manager']}>
                  <Succursale />
                  <Navigate to="/depot/succursale" replace />
                </ModuleProtectedRoute>
              } />

              <Route path="depot/manage-user" element={
                <ModuleProtectedRoute allowedRoles={['admin', 'super_admin', 'proprietaire', 'manager', 'depot']}>
                  <ManageUser />
                </ModuleProtectedRoute>
              } />

              <Route path="depot/sessions-caisse" element={
                <ModuleProtectedRoute allowedRoles={['admin', 'proprietaire', 'manager']}>
                  <SessionsCaissePage />
                </ModuleProtectedRoute>
              } />

              <Route path="abonnements" element={
                <ModuleProtectedRoute allowedRoles={['all']}>
                  <AbonnementPage />
                </ModuleProtectedRoute>
              } />
              {/* ========== MODULE RESTAURANT ========== */}
              <Route path="restaurant">
                {/* Redirection vers la première page accessible */}
                <Route index element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Navigate to="/restaurant/categories-menu" replace />
                  </ModuleProtectedRoute>
                } />
                
                {/* Sous-routes restaurant */}
                <Route path="categories-menu" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <CategoriesMenuPage />
                  </ModuleProtectedRoute>
                } />
                
                <Route path="categories-boissons" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <CategoriesBoissonsPage />
                  </ModuleProtectedRoute>
                } />
                
                <Route path="plats" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <PlatsPage />
                  </ModuleProtectedRoute>
                } />
                
                <Route path="boissons" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <BoissonsPage />
                  </ModuleProtectedRoute>
                } />
                
                <Route path="tables" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Tables />
                  </ModuleProtectedRoute>
                } />

                <Route path="chambres" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Chambres />
                  </ModuleProtectedRoute>
                } />

                <Route path="reservations" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Reservations />
                  </ModuleProtectedRoute>
                } />

                <Route path="commandes" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Commandes />
                  </ModuleProtectedRoute>
                } />

                <Route path="locations" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Locations />
                  </ModuleProtectedRoute>
                } />

                <Route path="employes" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Employes />
                  </ModuleProtectedRoute>
                } />

                <Route path="depenses" element={
                  <ModuleProtectedRoute requiredModule="restaurant">
                    <Depenses />
                  </ModuleProtectedRoute>
                } />
              </Route>


              {/* ========== MODULE GYM ========== */}
              <Route path="/gym/*" element={
                <ModuleProtectedRoute requiredModule="gym">
                  <GymRoutes />
                </ModuleProtectedRoute>
              } />

              {/* ========== MODULE INVENTAIRE (accessible par tous) ========== */}
              <Route path="/categories" element={
                <ModuleProtectedRoute requiredModule="inventaire">
                  <Categories />
                </ModuleProtectedRoute>
              } />
              
              {/* Routes pour Fournisseurs */}
              <Route path="fournisseurs">
                <Route index element={
                  <ModuleProtectedRoute requiredModule="inventaire">
                    <Fournisseurs />
                  </ModuleProtectedRoute>
                } />
                
                <Route path="liste" element={
                  <ModuleProtectedRoute requiredModule="inventaire">
                    <ListeFournisseurs />
                  </ModuleProtectedRoute>
                } />
                
                <Route path="achats" element={
                  <ModuleProtectedRoute requiredModule="inventaire">
                    <AchatsFournisseurs />
                  </ModuleProtectedRoute>
                } />
              </Route>

              <Route path="clients" element={
                <ModuleProtectedRoute requiredModule="inventaire">
                  <ClientsPage />
                </ModuleProtectedRoute>
              } />
              
              {/* <Route path="ventes" element={
                <ModuleProtectedRoute requiredModule="inventaire">
                  <VentesPage />
                </ModuleProtectedRoute>
              } /> */}

              <Route path="ventes" element={
                <ModuleProtectedRoute requiredModule="inventaire">
                  <VentesHistoriquePage />
                </ModuleProtectedRoute>
              } />

            <Route path="config-fidelite" element={
                <ModuleProtectedRoute requiredModule="inventaire">
                  <ConfigurationFidelite />
                </ModuleProtectedRoute>
              } />

              <Route path="/stocks-succursale" element={
                <ModuleProtectedRoute modules={['all', 'admin', 'inventaire', 'depot', 'shop', 'restaurant', 'gym', 'pharmacie']}>
                  <StockList />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/produits" element={
                <ModuleProtectedRoute requiredModule="inventaire">
                  <Produits />
                </ModuleProtectedRoute>
              } />

              {/* ========== ROUTES ADMIN/SUPERVISION (optionnelles) ========== */}
              {/* Ces routes pourraient être accessibles seulement par admin ou certains modules */}
              
              {/* Route accessible par plusieurs modules (ex: admin et inventaire) */}
              <Route path="/products" element={
                <ModuleProtectedRoute modules={['admin', 'inventaire']}>
                  <Products />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/customers" element={
                <ModuleProtectedRoute modules={['admin']}>
                  <Customers />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/transactions" element={
                <ModuleProtectedRoute modules={['admin', 'restaurant', 'shop', 'gym']}>
                  <Transactions />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/geography" element={
                <ModuleProtectedRoute modules={['admin']}>
                  <Geography />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/overview" element={
                <ModuleProtectedRoute modules={['admin']}>
                  <Overview />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/daily" element={
                <ModuleProtectedRoute modules={['admin']}>
                  <Daily />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/monthly" element={
                <ModuleProtectedRoute modules={['admin']}>
                  <Monthly />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/breakdown" element={
                <ModuleProtectedRoute modules={['admin']}>
                  <Breakdown />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/admin" element={
                <ModuleProtectedRoute requiredModule="admin">
                  <Admins />
                </ModuleProtectedRoute>
              } />
              
              <Route path="/performance" element={
                <ModuleProtectedRoute modules={['admin']}>
                  <Performance />
                </ModuleProtectedRoute>
              } />
            </Route>

            {/* Route par défaut */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;